import { supabase } from '@/integrations/supabase/client';

// All supported trigger types
export type LmsEmailTriggerType = 
  | 'enrollment' 
  | 'course_not_started'
  | 'lesson_added' 
  | 'lesson_not_completed'
  | 'quiz_not_started'
  | 'quiz_failed'
  | 'course_completion' 
  | 'certificate_issued'
  | 'approaching_expiry'
  | 'inactivity_reminder';

interface QueueEmailParams {
  brandId: string;
  courseId: string;
  triggerType: LmsEmailTriggerType;
  recipientEmail: string;
  recipientName: string;
  variables: Record<string, string>;
}

/**
 * Process template variables - replaces {{variable}} with actual values
 */
function processTemplate(template: string, variables: Record<string, string>): string {
  let result = template;
  for (const [key, value] of Object.entries(variables)) {
    const regex = new RegExp(`{{\\s*${key}\\s*}}`, 'gi');
    result = result.replace(regex, value || '');
  }
  return result;
}

/**
 * Wrap content in proper HTML email structure
 * Handles both plain text and rich text editor HTML output
 */
function wrapInEmailHtml(content: string): string {
  // Check if content already has full HTML document structure
  if (content.trim().startsWith('<!DOCTYPE') || content.trim().startsWith('<html')) {
    return content;
  }

  // Check if content has HTML tags (from rich text editor)
  const hasHtmlTags = /<[^>]+>/.test(content);
  
  let formattedContent: string;
  
  if (hasHtmlTags) {
    // Content is already HTML (from rich text editor) - just use it directly
    // Add inline styles for email compatibility
    formattedContent = content
      .replace(/<p>/g, '<p style="margin: 0 0 16px 0; line-height: 1.6; color: #333333;">')
      .replace(/<h1>/g, '<h1 style="margin: 0 0 16px 0; font-size: 24px; font-weight: bold; color: #111111;">')
      .replace(/<h2>/g, '<h2 style="margin: 0 0 14px 0; font-size: 20px; font-weight: bold; color: #111111;">')
      .replace(/<h3>/g, '<h3 style="margin: 0 0 12px 0; font-size: 18px; font-weight: bold; color: #111111;">')
      .replace(/<ul>/g, '<ul style="margin: 0 0 16px 0; padding-left: 24px;">')
      .replace(/<ol>/g, '<ol style="margin: 0 0 16px 0; padding-left: 24px;">')
      .replace(/<li>/g, '<li style="margin: 0 0 8px 0; line-height: 1.6; color: #333333;">')
      .replace(/<a /g, '<a style="color: #0066cc; text-decoration: underline;" ')
      .replace(/<blockquote>/g, '<blockquote style="margin: 0 0 16px 0; padding-left: 16px; border-left: 4px solid #e0e0e0; color: #666666;">');
  } else {
    // Plain text - convert newlines to paragraphs
    formattedContent = content
      .split('\n')
      .map(line => {
        const trimmedLine = line.trim();
        if (trimmedLine.length === 0) return '';
        return `<p style="margin: 0 0 16px 0; line-height: 1.6; color: #333333;">${trimmedLine}</p>`;
      })
      .filter(line => line.length > 0)
      .join('\n      ');
  }

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
</head>
<body style="margin: 0; padding: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; background-color: #f5f5f5;">
  <div style="max-width: 600px; margin: 0 auto; padding: 40px 20px;">
    <div style="background-color: #ffffff; border-radius: 8px; padding: 32px; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
      ${formattedContent}
    </div>
  </div>
</body>
</html>`;
}

/**
 * Queue an LMS email based on trigger type
 * This function finds matching email templates and queues them for sending
 */
export async function queueLmsEmail(params: QueueEmailParams): Promise<{ queued: number; errors: string[] }> {
  const { brandId, courseId, triggerType, recipientEmail, recipientName, variables } = params;
  
  const errors: string[] = [];
  let queued = 0;

  try {
    // Find active email templates for this trigger type and course
    const { data: emailTemplates, error: templatesError } = await supabase
      .from('os_lms_emails')
      .select('*')
      .eq('brand_id', brandId)
      .eq('course_id', courseId)
      .eq('trigger_type', triggerType)
      .eq('is_active', true)
      .order('sequence_order', { ascending: true });

    if (templatesError) {
      errors.push(`Failed to fetch email templates: ${templatesError.message}`);
      return { queued, errors };
    }

    if (!emailTemplates || emailTemplates.length === 0) {
      return { queued, errors };
    }

    for (const template of emailTemplates) {
      try {
        // Calculate scheduled time based on delay
        const scheduledFor = new Date();
        if (template.trigger_delay_days) {
          scheduledFor.setDate(scheduledFor.getDate() + template.trigger_delay_days);
        }
        if (template.trigger_delay_hours) {
          scheduledFor.setHours(scheduledFor.getHours() + template.trigger_delay_hours);
        }

        // Process template content with variables
        const processedSubject = processTemplate(template.subject || '', variables);
        const processedContent = processTemplate(template.content || '', variables);
        
        // Wrap in proper HTML structure for better email rendering
        const htmlContent = wrapInEmailHtml(processedContent);

        // Check if email already queued for this recipient + template combination today
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        const { data: existingEmail } = await supabase
          .from('os_lms_email_reminder_queue')
          .select('id')
          .eq('brand_id', brandId)
          .eq('recipient_email', recipientEmail)
          .eq('template_name', `lms_email_${template.id}`)
          .gte('created_at', today.toISOString())
          .maybeSingle();

        if (existingEmail) {
          continue;
        }

        // Queue the email
        const { error: queueError } = await supabase
          .from('os_lms_email_reminder_queue')
          .insert({
            brand_id: brandId,
            template_name: `lms_email_${template.id}`,
            recipient_email: recipientEmail,
            recipient_name: recipientName,
            subject: processedSubject,
            html_content: htmlContent,
            text_content: processedContent, // Plain text version for email clients that don't support HTML
            status: 'pending',
            priority: triggerType === 'enrollment' ? 9 : 5, // Higher priority for enrollment emails
            scheduled_for: scheduledFor.toISOString(),
            metadata: {
              email_template_id: template.id,
              trigger_type: triggerType,
              course_id: courseId,
              variables,
            },
          });

        if (queueError) {
          errors.push(`Failed to queue email: ${queueError.message}`);
        } else {
          queued++;
        }
      } catch (templateError: any) {
        errors.push(`Template ${template.id}: ${templateError.message}`);
      }
    }

    return { queued, errors };
  } catch (error: any) {
    errors.push(`Fatal error: ${error.message}`);
    return { queued, errors };
  }
}

/**
 * Queue enrollment email for a student
 */
export async function queueEnrollmentEmail(params: {
  brandId: string;
  courseId: string;
  courseTitle: string;
  studentEmail: string;
  studentName: string;
  loginLink?: string;
}): Promise<{ queued: number; errors: string[] }> {
  return queueLmsEmail({
    brandId: params.brandId,
    courseId: params.courseId,
    triggerType: 'enrollment',
    recipientEmail: params.studentEmail,
    recipientName: params.studentName,
    variables: {
      student_name: params.studentName,
      student_email: params.studentEmail,
      course_title: params.courseTitle,
      login_link: params.loginLink || '',
    },
  });
}

/**
 * Queue lesson added email for all enrolled students of a course
 */
export async function queueLessonAddedEmails(params: {
  brandId: string;
  courseId: string;
  courseTitle: string;
  lessonName: string;
}): Promise<{ queued: number; errors: string[] }> {
  const { brandId, courseId, courseTitle, lessonName } = params;
  const errors: string[] = [];
  let totalQueued = 0;

  try {
    // Get all active enrollments for this course
    const { data: enrollments, error: enrollmentsError } = await supabase
      .from('os_lms_enrollments')
      .select('student_name, student_email')
      .eq('brand_id', brandId)
      .eq('course_id', courseId)
      .eq('is_active', true);

    if (enrollmentsError) {
      errors.push(`Failed to fetch enrollments: ${enrollmentsError.message}`);
      return { queued: 0, errors };
    }

    if (!enrollments || enrollments.length === 0) {
      return { queued: 0, errors };
    }

    for (const enrollment of enrollments) {
      const result = await queueLmsEmail({
        brandId,
        courseId,
        triggerType: 'lesson_added',
        recipientEmail: enrollment.student_email,
        recipientName: enrollment.student_name || '',
        variables: {
          student_name: enrollment.student_name || '',
          student_email: enrollment.student_email,
          course_title: courseTitle,
          lesson_name: lessonName,
        },
      });

      totalQueued += result.queued;
      errors.push(...result.errors);
    }

    return { queued: totalQueued, errors };
  } catch (error: any) {
    errors.push(`Fatal error: ${error.message}`);
    return { queued: totalQueued, errors };
  }
}

/**
 * Queue course completion email
 */
export async function queueCourseCompletionEmail(params: {
  brandId: string;
  courseId: string;
  courseTitle: string;
  studentEmail: string;
  studentName: string;
}): Promise<{ queued: number; errors: string[] }> {
  return queueLmsEmail({
    brandId: params.brandId,
    courseId: params.courseId,
    triggerType: 'course_completion',
    recipientEmail: params.studentEmail,
    recipientName: params.studentName,
    variables: {
      student_name: params.studentName,
      student_email: params.studentEmail,
      course_title: params.courseTitle,
    },
  });
}

/**
 * Queue certificate issued email
 */
export async function queueCertificateIssuedEmail(params: {
  brandId: string;
  courseId: string;
  courseTitle: string;
  studentEmail: string;
  studentName: string;
  certificateUrl?: string;
}): Promise<{ queued: number; errors: string[] }> {
  return queueLmsEmail({
    brandId: params.brandId,
    courseId: params.courseId,
    triggerType: 'certificate_issued',
    recipientEmail: params.studentEmail,
    recipientName: params.studentName,
    variables: {
      student_name: params.studentName,
      student_email: params.studentEmail,
      course_title: params.courseTitle,
      certificate_url: params.certificateUrl || '',
    },
  });
}

/**
 * Queue quiz failed email
 */
export async function queueQuizFailedEmail(params: {
  brandId: string;
  courseId: string;
  courseTitle: string;
  quizTitle: string;
  studentEmail: string;
  studentName: string;
  score?: number;
  passingScore?: number;
}): Promise<{ queued: number; errors: string[] }> {
  return queueLmsEmail({
    brandId: params.brandId,
    courseId: params.courseId,
    triggerType: 'quiz_failed',
    recipientEmail: params.studentEmail,
    recipientName: params.studentName,
    variables: {
      student_name: params.studentName,
      student_email: params.studentEmail,
      course_title: params.courseTitle,
      quiz_title: params.quizTitle,
      score: params.score?.toString() || '',
      passing_score: params.passingScore?.toString() || '',
    },
  });
}

/**
 * Queue approaching expiry email - for enrollments about to expire
 */
export async function queueApproachingExpiryEmail(params: {
  brandId: string;
  courseId: string;
  courseTitle: string;
  studentEmail: string;
  studentName: string;
  expiryDate: string;
  daysRemaining: number;
}): Promise<{ queued: number; errors: string[] }> {
  return queueLmsEmail({
    brandId: params.brandId,
    courseId: params.courseId,
    triggerType: 'approaching_expiry',
    recipientEmail: params.studentEmail,
    recipientName: params.studentName,
    variables: {
      student_name: params.studentName,
      student_email: params.studentEmail,
      course_title: params.courseTitle,
      expiry_date: params.expiryDate,
      days_remaining: params.daysRemaining.toString(),
    },
  });
}
